import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, SpinnerGap } from '@phosphor-icons/react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const { clearCart } = useCart();
  const sessionId = searchParams.get('session_id');
  const hasPolled = useRef(false);

  const pollPaymentStatus = useCallback(async (sid, onClear, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentDetails(data);
        onClear();
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, onClear, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts >= maxAttempts - 1) {
        setStatus('failed');
      } else {
        setTimeout(() => pollPaymentStatus(sid, onClear, attempts + 1), pollInterval);
      }
    }
  }, []);

  useEffect(() => {
    if (sessionId && !hasPolled.current) {
      hasPolled.current = true;
      pollPaymentStatus(sessionId, clearCart);
    } else if (!sessionId) {
      setStatus('failed');
    }
  }, [sessionId, clearCart, pollPaymentStatus]);

  const formatPrice = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="checkout-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <div data-testid="payment-loading">
            <SpinnerGap size={64} className="animate-spin text-primary mx-auto mb-6" />
            <h1 
              className="text-3xl uppercase tracking-tight font-bold mb-4"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              PROCESSING PAYMENT
            </h1>
            <p className="text-muted-foreground">
              PLEASE WAIT WHILE WE CONFIRM YOUR ORDER...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success">
            <CheckCircle size={80} weight="fill" className="text-green-500 mx-auto mb-6" />
            <h1 
              className="text-4xl uppercase tracking-tight font-bold mb-4"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              ORDER CONFIRMED
            </h1>
            <p className="text-muted-foreground mb-2">
              THANK YOU FOR YOUR PURCHASE
            </p>
            {paymentDetails && (
              <p className="text-2xl font-bold mb-8" data-testid="payment-amount">
                {formatPrice(paymentDetails.amount_total)}
              </p>
            )}
            <p className="text-sm text-muted-foreground mb-8">
              YOU WILL RECEIVE A CONFIRMATION EMAIL SHORTLY.
            </p>
            <Link to="/">
              <Button 
                className="bg-white text-black hover:bg-primary hover:text-white transition-colors h-12 px-8"
                data-testid="continue-shopping-btn"
              >
                CONTINUE SHOPPING
              </Button>
            </Link>
          </div>
        )}

        {status === 'failed' && (
          <div data-testid="payment-failed">
            <XCircle size={80} weight="fill" className="text-destructive mx-auto mb-6" />
            <h1 
              className="text-4xl uppercase tracking-tight font-bold mb-4"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              PAYMENT FAILED
            </h1>
            <p className="text-muted-foreground mb-8">
              SOMETHING WENT WRONG WITH YOUR PAYMENT. PLEASE TRY AGAIN.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/checkout">
                <Button 
                  className="bg-white text-black hover:bg-primary hover:text-white transition-colors"
                  data-testid="try-again-btn"
                >
                  TRY AGAIN
                </Button>
              </Link>
              <Link to="/">
                <Button 
                  variant="outline"
                  className="border-border hover:bg-muted"
                  data-testid="back-home-btn"
                >
                  BACK TO HOME
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
